/**
 * Portal.tsx — "The Gateway" Easter Egg
 * 
 * Inspired by Valve's Portal game. The portal is an oval-shaped anomaly
 * that appears in the footer. Unlike the previous version, this one:
 * 
 * LAYOUT: Warning text on the LEFT, oval portal on the RIGHT.
 * 
 * ACTIVATION SEQUENCE (5 seconds total, hover-triggered):
 *   Phase 0 (0s):     Idle — small oval portal pulsates quietly.
 *   Phase 1 (hover):  Warning appears on left; portal edges glow brighter.
 *   Phase 2 (2s):     Portal grows slowly, edges intensify.
 *   Phase 3 (3.5s):   Screen starts shaking (earthquake). Elements jitter.
 *   Phase 4 (4.5s):   Text elements "fall" from the page. Portal consumes screen.
 *   Phase 5 (5.5s):   Full chaos. Then redirect to backend.lehana.in.
 * 
 * KEY DESIGN DECISIONS:
 * - Oval shape via `rounded-[50%]` with width > height (like Portal game)
 * - Multiple concentric rings with counter-rotating animations
 * - Inner vortex with conic gradient spinning effect
 * - Earthquake uses CSS keyframe on root element for maximum chaos
 * - "Falling text" simulated by applying gravity-like transforms to all text nodes
 * - No white flash — instead, black void with portal color bleeding out
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Activation phase enum for clarity ---
type PortalPhase = 'idle' | 'warning' | 'growing' | 'earthquake' | 'collapse' | 'consumed';

export function Portal() {
    const [hovered, setHovered] = useState(false);
    const [phase, setPhase] = useState<PortalPhase>('idle');
    const timerRefs = useRef<number[]>([]);

    // Cleanup all timers on unmount or when user leaves
    const clearAllTimers = () => {
        timerRefs.current.forEach(t => clearTimeout(t));
        timerRefs.current = [];
    };

    // --- HOVER START: Begin the 5-phase countdown ---
    const handleMouseEnter = () => {
        if (phase !== 'idle') return; // Don't restart if already activating
        setHovered(true);
        setPhase('warning');

        // Phase 2: Growing (2s after hover)
        timerRefs.current.push(window.setTimeout(() => {
            setPhase('growing');
        }, 2000));

        // Phase 3: Earthquake (3.5s after hover)
        timerRefs.current.push(window.setTimeout(() => {
            setPhase('earthquake');
            // Add shake class to entire page for maximum chaos
            document.documentElement.classList.add('portal-earthquake');
        }, 3500));

        // Phase 4: Collapse — text falls, portal expands (4.5s)
        timerRefs.current.push(window.setTimeout(() => {
            setPhase('collapse');
        }, 4500));

        // Phase 5: Consumed — redirect (5.5s total)
        timerRefs.current.push(window.setTimeout(() => {
            setPhase('consumed');
            // Remove earthquake before redirect
            document.documentElement.classList.remove('portal-earthquake');
            setTimeout(() => {
                window.location.href = 'https://backend.lehana.in';
            }, 800);
        }, 5500));
    };

    // --- HOVER LEAVE: Cancel activation if not too far gone ---
    const handleMouseLeave = () => {
        // Only allow cancellation during early phases
        if (phase === 'warning' || phase === 'growing') {
            clearAllTimers();
            setHovered(false);
            setPhase('idle');
            document.documentElement.classList.remove('portal-earthquake');
        }
        // During earthquake/collapse/consumed, it's too late to cancel
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearAllTimers();
            document.documentElement.classList.remove('portal-earthquake');
        };
    }, []);

    // Calculate portal scale based on phase
    const getPortalScale = () => {
        switch (phase) {
            case 'idle': return 1;
            case 'warning': return 1.2;
            case 'growing': return 2;
            case 'earthquake': return 3;
            case 'collapse': return 8;
            case 'consumed': return 60;
        }
    };

    // Calculate portal glow intensity
    const getGlowIntensity = () => {
        switch (phase) {
            case 'idle': return 0.3;
            case 'warning': return 0.5;
            case 'growing': return 0.7;
            case 'earthquake': return 1;
            case 'collapse': return 1.5;
            case 'consumed': return 2;
        }
    };

    const isActivated = phase !== 'idle';
    const isPastPointOfNoReturn = phase === 'earthquake' || phase === 'collapse' || phase === 'consumed';

    return (
        <>
            {/* Main Portal Area — Warning left, Portal right */}
            <div
                className="relative flex items-center justify-center gap-8 md:gap-16 py-12 max-w-xl mx-auto"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {/* LEFT SIDE: Warning Text */}
                <AnimatePresence>
                    {hovered && phase !== 'consumed' && (
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -40, y: isPastPointOfNoReturn ? 200 : 0 }}
                            transition={{ duration: 0.5 }}
                            className="text-right max-w-[200px] z-20 pointer-events-none select-none"
                        >
                            <motion.p
                                animate={isPastPointOfNoReturn ? {
                                    y: [0, 2, -2, 3, -1, 0],
                                    rotate: [0, -1, 1, -2, 2, 0],
                                } : {}}
                                transition={{ repeat: Infinity, duration: 0.3 }}
                                className="text-red-500 font-mono text-xs tracking-widest uppercase bg-black/80 px-4 py-2 rounded border border-red-500/30 mb-2 whitespace-nowrap"
                            >
                                ⚠ DO NOT LINGER
                            </motion.p>
                            <p className="text-[10px] text-gray-500 leading-tight font-mono">
                                {phase === 'warning' && 'comprehending the secrets of Paras Lehana may cause system instability...'}
                                {phase === 'growing' && 'CRITICAL: Dimensional breach detected. Move away NOW.'}
                                {phase === 'earthquake' && '█ SYSTEM FAILURE █ STRUCTURAL INTEGRITY COMPROMISED'}
                                {phase === 'collapse' && '[ FATAL ] ALL SYSTEMS OFFLINE — EVACUATE'}
                            </p>

                            {/* Progress indicator */}
                            {isActivated && (
                                <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-red-500"
                                        initial={{ width: '0%' }}
                                        animate={{ width: '100%' }}
                                        transition={{ duration: 5.5, ease: 'linear' }}
                                    />
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* RIGHT SIDE: The Oval Portal */}
                <motion.div
                    animate={{
                        scale: getPortalScale(),
                        rotate: phase === 'consumed' ? 180 : 0,
                    }}
                    transition={{
                        duration: isPastPointOfNoReturn ? 1.5 : 0.8,
                        ease: isPastPointOfNoReturn ? [0.4, 0, 0.2, 1] : 'easeInOut',
                    }}
                    className="relative cursor-pointer z-10"
                    style={{
                        // Oval shape — wider than tall, like Portal game
                        width: '44px',
                        height: '64px',
                    }}
                >
                    {/* Outer glow ring */}
                    <div
                        className="absolute inset-[-8px] rounded-[50%] transition-all duration-500"
                        style={{
                            background: `radial-gradient(ellipse, transparent 40%, rgba(139,92,246,${getGlowIntensity() * 0.3}))`,
                            boxShadow: `0 0 ${20 * getGlowIntensity()}px ${8 * getGlowIntensity()}px rgba(139,92,246,${getGlowIntensity() * 0.4}), 
                                         0 0 ${40 * getGlowIntensity()}px ${16 * getGlowIntensity()}px rgba(168,85,247,${getGlowIntensity() * 0.2})`,
                        }}
                    />

                    {/* Core oval — black void center */}
                    <div
                        className="absolute inset-0 rounded-[50%] bg-black"
                        style={{
                            border: `2px solid rgba(139,92,246,${0.6 + getGlowIntensity() * 0.2})`,
                            boxShadow: `inset 0 0 15px rgba(139,92,246,${getGlowIntensity() * 0.5}),
                                         0 0 30px rgba(139,92,246,${getGlowIntensity() * 0.3})`,
                        }}
                    />

                    {/* Spinning outer ring 1 */}
                    <div
                        className="absolute inset-[-3px] rounded-[50%] border-2 border-transparent animate-spin"
                        style={{
                            animationDuration: '3s',
                            borderTopColor: `rgba(139,92,246,${0.5 + getGlowIntensity() * 0.2})`,
                            borderLeftColor: `rgba(192,132,252,${0.3 + getGlowIntensity() * 0.2})`,
                        }}
                    />

                    {/* Spinning outer ring 2 (counter-clockwise) */}
                    <div
                        className="absolute inset-[-6px] rounded-[50%] border-2 border-transparent animate-spin"
                        style={{
                            animationDuration: '2s',
                            animationDirection: 'reverse',
                            borderRightColor: `rgba(232,121,249,${0.4 + getGlowIntensity() * 0.2})`,
                            borderBottomColor: `rgba(168,85,247,${0.3 + getGlowIntensity() * 0.2})`,
                        }}
                    />

                    {/* Inner vortex */}
                    <div className="absolute inset-2 rounded-[50%] overflow-hidden">
                        <div
                            className="absolute inset-[-50%] animate-spin"
                            style={{
                                animationDuration: isPastPointOfNoReturn ? '0.3s' : '1.5s',
                                background: `conic-gradient(from 0deg, transparent, rgba(139,92,246,${0.3 + getGlowIntensity() * 0.3}), transparent, rgba(192,132,252,${0.2 + getGlowIntensity() * 0.2}), transparent)`,
                            }}
                        />
                    </div>

                    {/* Center singularity — pulsing dot */}
                    <motion.div
                        animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.6, 1, 0.6],
                        }}
                        transition={{ repeat: Infinity, duration: isPastPointOfNoReturn ? 0.3 : 1.5 }}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-violet-400"
                        style={{
                            boxShadow: `0 0 8px 2px rgba(139,92,246,${getGlowIntensity()})`,
                        }}
                    />

                    {/* Edge particles — only during activation */}
                    {isPastPointOfNoReturn && (
                        <>
                            {[...Array(8)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-1 h-1 rounded-full bg-violet-400"
                                    style={{
                                        top: '50%',
                                        left: '50%',
                                    }}
                                    animate={{
                                        x: [0, Math.cos(i * 45 * Math.PI / 180) * 30],
                                        y: [0, Math.sin(i * 45 * Math.PI / 180) * 40],
                                        opacity: [1, 0],
                                        scale: [1, 0.3],
                                    }}
                                    transition={{
                                        repeat: Infinity,
                                        duration: 0.8,
                                        delay: i * 0.1,
                                        ease: 'easeOut',
                                    }}
                                />
                            ))}
                        </>
                    )}
                </motion.div>
            </div>

            {/* COLLAPSE PHASE: Falling text simulation */}
            {(phase === 'collapse' || phase === 'consumed') && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 z-[9998] pointer-events-none"
                >
                    {/* Chaos overlay — dark void with purple bleeding */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: phase === 'consumed' ? 1 : 0.6,
                            background: phase === 'consumed'
                                ? 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, black 70%)'
                                : 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
                        }}
                        transition={{ duration: 1 }}
                        className="absolute inset-0"
                    />

                    {/* Glitch scanlines */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(139,92,246,0.03) 2px, rgba(139,92,246,0.03) 4px)',
                            animation: 'glitch-scanline 0.1s linear infinite',
                        }}
                    />

                    {/* Random glitch blocks */}
                    {phase === 'consumed' && (
                        <>
                            {[...Array(5)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute bg-violet-500/20"
                                    style={{
                                        width: `${20 + Math.random() * 60}%`,
                                        height: `${2 + Math.random() * 8}px`,
                                        top: `${Math.random() * 100}%`,
                                        left: `${Math.random() * 40}%`,
                                    }}
                                    animate={{
                                        x: [0, 20, -10, 30, 0],
                                        opacity: [0, 1, 0, 1, 0],
                                    }}
                                    transition={{
                                        repeat: Infinity,
                                        duration: 0.3 + Math.random() * 0.4,
                                        delay: Math.random() * 0.5,
                                    }}
                                />
                            ))}
                        </>
                    )}
                </motion.div>
            )}
        </>
    );
}
